import { join } from 'path';

import { X_PLANERA_ORGANIZATION_ID, DO_NOT_SEND_HEADER, X_PLANERA_WORKSPACE_ID } from 'state_management/threadedConstants';

import { sendRequest } from '../core';
import { RoleAlias } from '../user_organization_details/types';
import { DeleteInviteRequest, SendInviteRequest } from './requests';
import { AcceptInviteRequest, InviteResponse, PendingInvites } from './responses';

export async function acceptInvites(invites: AcceptInviteRequest[]) {
  return sendRequest(
    'POST',
    `/api/v2/invite/accept`,
    invites.map(({ email, orgId, role }) => ({ email, orgId, role }))
  );
}

export async function rejectInvite(email: string, organizationId: string, role: RoleAlias) {
  return sendRequest('DELETE', `/api/v1/invite`, [
    {
      email,
      orgId: organizationId,
      role
    }
  ]);
}

export async function getPendingInvites() {
  return sendRequest<PendingInvites>('GET', '/api/v1/pending-invites', undefined, {
    [X_PLANERA_ORGANIZATION_ID]: DO_NOT_SEND_HEADER,
    [X_PLANERA_WORKSPACE_ID]: DO_NOT_SEND_HEADER
  });
}

export async function getUserHash() {
  return sendRequest<{ token: string }>('GET', '/api/v1/user/intercom-hmac', undefined);
}

export async function sendInvites(organizationID: string, invites: SendInviteRequest[]) {
  return sendRequest<InviteResponse[]>('POST', `/api/v2/invite`, invites, {
    [X_PLANERA_ORGANIZATION_ID]: organizationID
  });
}

export async function resendInvites(email: string, orgId: string, role: RoleAlias) {
  return sendRequest<InviteResponse>(
    'POST',
    `api/v2/resend-invite`,
    { email, orgId, role },
    {
      [X_PLANERA_ORGANIZATION_ID]: orgId
    }
  );
}

export async function deleteInvites(organizationID: string, invites: DeleteInviteRequest[]) {
  return sendRequest<InviteResponse[]>('DELETE', '/api/v1/invite', invites, {
    [X_PLANERA_ORGANIZATION_ID]: organizationID
  });
}

export async function removeUserOrganizationRole(authUserId: string, organizationId: string) {
  return sendRequest('DELETE', join('/api/v1/user', authUserId, '/organization/', organizationId), undefined, {
    [X_PLANERA_ORGANIZATION_ID]: organizationId
  });
}

export async function removeUserOrganizationRoleAndTransfer(authUserId: string, organizationId: string, transferUserId: string) {
  return sendRequest('PATCH', join('/api/v1/user', authUserId, '/organization/', organizationId, '/owner/', transferUserId), undefined, {
    [X_PLANERA_ORGANIZATION_ID]: organizationId
  });
}

export async function transferUserProjectsAndRemoveRole(authUserId: string, newUserId: string, organizationId: string) {
  return sendRequest('PATCH', join('/api/v1/user', authUserId, '/transfer-projects/', newUserId), undefined, {
    [X_PLANERA_ORGANIZATION_ID]: organizationId
  });
}

export async function removeUserProjectRole(authUserId: string, organizationId: string, workspaceId: string, projectId: string) {
  return sendRequest('DELETE', join('/api/v1/user', authUserId, '/project/', projectId), undefined, {
    [X_PLANERA_ORGANIZATION_ID]: organizationId,
    [X_PLANERA_WORKSPACE_ID]: workspaceId
  });
}

export async function changeUserOrganizationRole(authUserId: string, roleAlias: RoleAlias, organizationId: string) {
  return sendRequest(
    'PUT',
    join('/api/v1/user', authUserId, '/organization/', organizationId),
    {
      userId: authUserId,
      orgId: organizationId,
      roleAlias: roleAlias
    },
    {
      [X_PLANERA_ORGANIZATION_ID]: organizationId
    }
  );
}

export async function changeUserProjectRole(authUserId: string, roleAlias: RoleAlias, organizationId: string, workspaceId: string, projectId: string, pending: boolean = false) {
  return sendRequest(
    'PUT',
    join('/api/v1/user', authUserId, '/project/', projectId),
    {
      userId: authUserId,
      orgId: organizationId,
      roleAlias: roleAlias,
      pending
    },
    {
      [X_PLANERA_ORGANIZATION_ID]: organizationId,
      [X_PLANERA_WORKSPACE_ID]: workspaceId
    }
  );
}
